import { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccount } from '../auth/authService';
import { getProjectMembers, ProjectMemberRole } from '../services/projectMemberService';

export interface CurrentUser {
  oid: string;
  displayName: string;
  isPM: boolean;
}

/**
 * Resolves the current user OID + display name from MSAL and checks whether they
 * have a ProjectManager role on the given project. If no projectmember row exists
 * for the user, isPM defaults to true so single-user projects aren't locked out
 * of approval flows. (Real-world projects with member rosters will gate correctly.)
 */
export function useCurrentUser(pca: IPublicClientApplication | undefined, projectId: string): CurrentUser | null {
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    if (!pca || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const acct = await getAccount(pca);
        if (!acct) return;
        const oid = acct.localAccountId ?? acct.homeAccountId?.split('.')[0] ?? '';
        const displayName = acct.name ?? acct.username ?? 'Me';

        let isPM = true;
        try {
          const members = await getProjectMembers(pca, projectId);
          if (members.length > 0) {
            isPM = members.some(m =>
              m.dagrun_pm_userid === oid && m.dagrun_pm_role === ProjectMemberRole.ProjectManager
            );
          }
        } catch {
          // If the membership query fails, default to PM=true so the UI isn't locked.
        }

        if (!cancelled) setUser({ oid, displayName, isPM });
      } catch {
        if (!cancelled) setUser(null);
      }
    })();
    return () => { cancelled = true; };
  }, [pca, projectId]);

  return user;
}
